import { TrendingDown, TrendingUp } from 'lucide-react'
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

type ChartRow = {
  name: string
  value: number
  color: string
}

const EMPTY_CHART_DATA: ChartRow[] = [
  {
    name: 'No changes',
    value: 1,
    color: 'hsl(var(--muted))',
  },
]

export function WeeklyCommitsPieChart({
  totalCommits,
  trend,
  chartData,
}: {
  totalCommits: number
  trend: string
  chartData: ChartRow[]
}) {
  const hasCommits = totalCommits > 0
  const pieData = hasCommits ? chartData : EMPTY_CHART_DATA
  const parsedTrend = Number.parseFloat(trend)
  const trendValue = Number.isFinite(parsedTrend) ? parsedTrend : 0
  const trendDirection = trendValue > 0 ? 'up' : trendValue < 0 ? 'down' : 'flat'
  const trendColor =
    trendDirection === 'up' ? 'text-green-700' : trendDirection === 'down' ? 'text-red-600' : 'text-muted-foreground'
  const TrendIconComponent = trendDirection === 'up' ? TrendingUp : trendDirection === 'down' ? TrendingDown : null
  const formattedTrendValue = Math.abs(trendValue).toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })

  return (
    <Card className="flex flex-col">
      <CardHeader className="items-center pb-4 text-center">
        <CardTitle>Code Changes Distribution</CardTitle>
        <CardDescription>Analysis of commit types over the last 14 days</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 items-center justify-center pb-2">
        <div className="mx-auto aspect-square w-full max-w-[260px] [&_.recharts-layer]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-wrapper]:outline-none [&_svg]:outline-none [&_svg:focus]:outline-none">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              {hasCommits ? (
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                  }}
                />
              ) : null}
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={100}
                strokeWidth={4}
                isAnimationActive={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
                ))}
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) - 10}
                            className="fill-foreground text-3xl font-bold"
                          >
                            {totalCommits.toLocaleString()}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 15} className="fill-muted-foreground text-sm">
                            Changes
                          </tspan>
                        </text>
                      )
                    }
                    return null
                  }}
                />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
      <CardFooter className="flex-col items-center gap-2 pt-4 text-sm">
        <div className="flex items-center gap-2 text-center font-medium">
          <span className="leading-none">
            {hasCommits ? (
              trendDirection === 'flat' ? (
                'No change from last week'
              ) : (
                <>
                  Trending {trendDirection} by <span className={trendColor}>{formattedTrendValue}%</span> this week
                </>
              )
            ) : (
              'No code changes recorded in the last 14 days'
            )}
          </span>
          {hasCommits && TrendIconComponent ? <TrendIconComponent className={`size-4 ${trendColor}`} /> : null}
        </div>
        <div className="text-muted-foreground text-center leading-none">
          Showing code changes distribution for the last 14 days
        </div>
      </CardFooter>
    </Card>
  )
}
